import { forwardRef, useRef, useImperativeHandle } from 'react';
import { useReqireLabel, useTracking, getLabel } from '../utils';
import { Chevron } from '../';
import type { TrackingProp } from '../types';
import styles from '../input/input.module.css';
import clsx from 'clsx';

export type SelectProps = Omit<
  React.ComponentPropsWithoutRef<'select'>,
  'size' | 'multiple' | 'prefix' // Do not support multiple select
> & {
  internal?: 'small' | false;
  invalid?: boolean;
  prefix?: React.ReactNode;
  size?: number | string | 'max-content';
  tracking?: TrackingProp;
  wrapperProps?: React.ComponentPropsWithoutRef<'span'>;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    {
      hidden,
      internal = false,
      invalid,
      onChange,
      prefix,
      size,
      style,
      tracking = true,
      wrapperProps,
      ...rest
    },
    ref
  ) {
    const innerRef = useRef<HTMLSelectElement>(null);
    const track = useTracking(tracking);
    const classes = clsx(
      wrapperProps?.className,
      styles.wrapper,
      !!size && styles.htmlSize,
      !!rest.disabled && styles.isDisabled,
      !!invalid && styles.isInvalid,
      !!internal && styles[`${internal}`]
    );

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      track({
        eventCategory: 'select',
        eventAction: 'change',
        eventLabel: getLabel(event.target),
        eventStatus:
          event.target?.options?.[event.target.selectedIndex].text.trim() || ''
      });
      onChange?.(event);
    };

    useImperativeHandle(ref, () => innerRef.current as HTMLSelectElement); // Forward innerRef
    useReqireLabel<HTMLSelectElement>(innerRef);

    return (
      <span
        {...wrapperProps} // Must be before className
        className={classes}
        hidden={hidden}
      >
        {prefix}
        <select
          data-internal-small={internal === 'small' ? true : undefined}
          style={{ ...style, '--size': size } as React.CSSProperties}
          onChange={handleChange}
          ref={innerRef}
          {...rest}
        />
        <Chevron className={styles.chevron} />
      </span>
    );
  }
);
